// verts_t - verts_0, rasterised into the head UV atlas -> the `disp_map` input
// the v16 --head_local_disp geom/appr decoders take.
//
// This is the browser twin of TAs ae/local_displacement.py:displacement_uv_map
// (the RAW field, rigid_align=false path the myra_v16 full_v2 head trained with)
// followed by ae/local_displacement.py:soft_clip. Where the OLD --head_mesh_cond
// head reduced the whole field to a 256-d code through a trunk (headDisplacement.js),
// this head keeps the field DENSE: every texel's Gaussian sees its own local
// motion, fused mid-decoder (ae/simple_decoder.py:_fuse_disp). So nothing here may
// pool or resample the field -- it is barycentric-interpolated into the atlas and
// fed straight to the decoder.
//
// UNITS. The output is in METRES (verts are metres; world_scale 1.0). The exported
// geom/appr graphs divide by DISP_UNIT_M=1e-3 internally (baked from _fuse_disp),
// so DO NOT pre-scale to mm here -- feed the soft-clipped metre field. soft_clip
// caps the fuse INPUT at clip_mm (5 mm for full_v2): clip_m*tanh(v/clip_m), unit
// slope at 0 so expression-scale motion (1-3 mm) passes almost unchanged while the
// head's larger rigid motion saturates. clip_mm<=0 disables it.
//
// LAYOUT. `out` is NCHW (1,3,H,W) flattened: out[c*H*W + y*W + x], matching the
// ONNX `disp_map` input. Texels outside the atlas (triim < 0) are exactly 0.

/** Rasterise the per-texel local displacement field.
 *
 *  vertsT, vertsRest : Float32Array (V*3)  posed / rest vertices, xyz-interleaved
 *  triim             : Int32Array   (H*W)  triangle id per texel, <0 outside atlas
 *  barim             : Float32Array (H*W*3) barycentric weights of the hit triangle
 *  faces             : Uint32Array  (F*3)  vertex ids per triangle
 *  H, W              : atlas resolution (square: H===W for the head)
 *  clipMm            : soft-clip bound in mm (0 = off)
 *  out               : optional reused Float32Array(3*H*W); allocated if absent
 *
 *  Returns the NCHW Float32Array. Returns null on a shape disagreement so a caller
 *  cannot feed a half-built map to the decoder (mirrors computeDisplacement). */
export function buildHeadDispMap(
  { vertsT, vertsRest, triim, barim, faces, H, W, clipMm = 0 },
  out,
) {
  if (!vertsT || !vertsRest || vertsT.length !== vertsRest.length) return null;
  if (!triim || triim.length !== H * W) return null;
  if (!barim || barim.length !== H * W * 3) return null;
  const n = H * W;
  const dst = out && out.length === 3 * n ? out : new Float32Array(3 * n);
  dst.fill(0);
  const clipM = clipMm > 0 ? clipMm * 1e-3 : 0;
  for (let p = 0; p < n; p++) {
    const tri = triim[p];
    if (tri < 0) continue; // outside the atlas -> stays 0
    const f = tri * 3;
    const a = faces[f] * 3;
    const b = faces[f + 1] * 3;
    const c = faces[f + 2] * 3;
    const w0 = barim[p * 3];
    const w1 = barim[p * 3 + 1];
    const w2 = barim[p * 3 + 2];
    for (let k = 0; k < 3; k++) {
      const d0 = vertsT[a + k] - vertsRest[a + k];
      const d1 = vertsT[b + k] - vertsRest[b + k];
      const d2 = vertsT[c + k] - vertsRest[c + k];
      let v = w0 * d0 + w1 * d1 + w2 * d2;
      if (clipM > 0) v = clipM * Math.tanh(v / clipM);
      dst[k * n + p] = v; // NCHW: channel-major
    }
  }
  return dst;
}
